import React, { useRef, useState } from "react";
import { connect } from "react-redux";
import { Form } from "react-final-form";

import Logo from "./Logo";
import "./AuthFlow.css";

import IMG_LOCATION_BG from "../../assets/images/location_bg.png";

import BusinessCategoriesField from "containers/BusinessCategoriesField/BusinessCategoriesField";
import { actions } from "coupon-common/src/modules/businessInfo";
import { dispatchAsAsync } from "coupon-common/src/utils";

function UploadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="1" y="1" width="18" height="18" rx="9" fill="#FBE4E1" />
      <path d="M10 5.5v7M7.2 8.3L10 5.5l2.8 2.8" stroke="#C0392B" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 13.2v.8a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5v-.8" stroke="#C0392B" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7.2l3 3L11.5 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function SkipArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3 7h8M8 3.5L11.5 7 8 10.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function UploadBox({ label, previewSrc, onFile }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const acceptFile = (file) => {
    if (file && file.type.startsWith("image/")) onFile?.(file);
  };

  return (
    <div className="auth-upload-block">
      <div className="auth-field-label">{label}</div>
      <div
        className={`auth-upload-box${dragging ? " auth-upload-box-dragging" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          acceptFile(e.dataTransfer.files?.[0]);
        }}
      >
        {previewSrc && <img src={previewSrc} alt="" className="auth-upload-preview" />}
        <div className="auth-upload-drop" onClick={() => inputRef.current?.click()}>
          <UploadIcon />
          <p>
            <a href="#" onClick={(e) => { e.preventDefault(); inputRef.current?.click(); }}>Click here</a> to upload your file or drag.
          </p>
          <span>Supported Format: SVG, JPG, PNG (10mb each)</span>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            acceptFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

export const MediaUploadPage = ({ onRegister, dispatch, businessData }) => {
  // File uploads live outside the form — they aren't react-final-form
  // fields, just plain local state merged into the payload on submit.
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const onSubmit = async (values) => {
    setLoading(true);
    setErrorMessage("");

    try {
      // Uploading is optional — logoFile / bannerFile may be null,
      // and categories may be empty. Submitting with nothing filled
      // in is effectively a "skip".
      //
      // formatToAccountFormData (coupon-common/src/modules/businessInfo/api)
      // only special-cases the exact keys "logo" and "cover_image" to run
      // them through formatToImageArgs — any other key name falls through
      // to a plain data.append(key, value), which sends the file under
      // the wrong field name. Rename at the boundary so the local state
      // names (logoFile/bannerFile) can stay descriptive.
      const payload = {
          ...businessData,
          ...values,
          ...(logoFile ? { logo: logoFile } : {}),
          ...(bannerFile ? { cover_image: bannerFile } : {}),
        };

        const submitErrors = await dispatchAsAsync(actions.updateInfo(payload), dispatch);

      if (submitErrors) {
        console.log("ERROR: " + JSON.stringify(submitErrors));  
        setErrorMessage(submitErrors.message || "Something went wrong. Please try again.");
      } else {
        onRegister?.({ ...values, logoFile, bannerFile });
      }
    } catch (err) {
      console.log("Error: " + err);
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page auth-simple-page">
      <img src={IMG_LOCATION_BG} alt="" className="auth-simple-bg" />
      <header className="auth-navbar auth-navbar-simple">
        <Logo />
      </header>

      <div className="auth-card-wrap auth-card-wrap-loose">
        <div className="auth-card auth-card-wide">
          <h2 className="auth-form-title">Add Your Branding</h2>
          <p className="auth-section-subtext">
            Optional — you can skip this and add it later.
          </p>

          {/* BusinessCategoriesField relies on react-final-form's
              useForm()/useFormState(), so it must render inside a
              <Form> — everything below moved into this wrapper. */}
          <Form
            onSubmit={onSubmit}
            render={({ handleSubmit, values }) => {
              // NOTE: assumes BusinessCategoriesField registers its
              // selections under the field name "categories" as an
              // array. Update this key if the real field name differs.
              const hasCategories = Array.isArray(values.categories) && values.categories.length > 0;
              const hasUploads = !!logoFile || !!bannerFile;
              const hasAnyData = hasUploads || hasCategories;

              return (
                <form onSubmit={handleSubmit}>
                  <div className="auth-upload-row">
                    <UploadBox
                      label="Logo"
                      previewSrc={logoFile ? URL.createObjectURL(logoFile) : null}
                      onFile={setLogoFile}
                    />
                    <UploadBox
                      label="Banner"
                      previewSrc={bannerFile ? URL.createObjectURL(bannerFile) : null}
                      onFile={setBannerFile}
                    />
                  </div>

                  <BusinessCategoriesField />

                  {errorMessage && (
                    <div style={{ color: "red", margin: "5px", fontSize: "11px" }}>
                      {errorMessage}
                    </div>
                  )}

                  <div className="auth-form-actions">
                    <button
                      type="submit"
                      className="auth-btn auth-btn-solid"
                      disabled={loading}
                    >
                      {loading
                        ? "Registering..."
                        : hasAnyData
                        ? <>Register <CheckIcon /></>
                        : <>Skip <SkipArrowIcon /></>}
                    </button>
                  </div>
                </form>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default connect(state => ({ businessData: state.businessInfo.data }))(MediaUploadPage);